/**
 * Shared Coveo Headless facet controllers for the CSR arm.
 *
 * Controllers are created as singletons keyed by field name so that
 * both the horizontal filters and the FiltersDrawer share the same
 * state. The URL manager syncs engine state with the URL hash, so
 * selections made in either UI are reflected everywhere.
 *
 * The range data and label helpers live in utils/facetRanges and
 * utils/facetLabels, shared with the SSR arm.
 */
import {
  DateFacet,
  Facet,
  NumericFacet,
  SearchEngine,
  buildDateFacet,
  buildDateRange,
  buildFacet,
  buildNumericFacet,
  buildNumericRange,
} from '@coveo/atomic-react';
import {
  DATE_RANGE_KEYS,
  PRICE_RANGES,
  RATING_RANGES,
} from './utils/facetRanges';

const DAY_MS = 24 * 60 * 60 * 1000;

// Per-engine controller caches — keyed by engine instance so search and
// listing engines each get their own independent set of controllers.
type ControllerCache = {
  price: NumericFacet | null;
  rating: NumericFacet | null;
  publisher: Facet | null;
  releaseDate: DateFacet | null;
  category: Facet | null;
  sale: Facet | null;
};

const cacheByEngine = new WeakMap<object, ControllerCache>();

function getCache(eng: SearchEngine): ControllerCache {
  if (!cacheByEngine.has(eng)) {
    cacheByEngine.set(eng, {
      price: null,
      rating: null,
      publisher: null,
      releaseDate: null,
      category: null,
      sale: null,
    });
  }
  return cacheByEngine.get(eng)!;
}

export function getPriceController(eng: SearchEngine): NumericFacet {
  const cache = getCache(eng);
  if (!cache.price) {
    cache.price = buildNumericFacet(eng, {
      options: {
        field: 'ec_price_filter',
        generateAutomaticRanges: false,
        currentValues: PRICE_RANGES.map((range) =>
          buildNumericRange({
            start: range.start,
            end: range.end,
            endInclusive: range.start === range.end || range.end === 999999,
          })
        ),
      },
    });
  }
  return cache.price;
}

export function getRatingController(eng: SearchEngine): NumericFacet {
  const cache = getCache(eng);
  if (!cache.rating) {
    cache.rating = buildNumericFacet(eng, {
      options: {
        field: 'ec_rating',
        generateAutomaticRanges: false,
        currentValues: RATING_RANGES.map((range) =>
          buildNumericRange({
            start: range.start,
            end: range.end,
            endInclusive: true,
          })
        ),
      },
    });
  }
  return cache.rating;
}

export function getPublisherController(eng: SearchEngine): Facet {
  const cache = getCache(eng);
  if (!cache.publisher) {
    cache.publisher = buildFacet(eng, {
      options: {
        field: 'publisher_name',
        numberOfValues: 1000,
        sortCriteria: 'occurrences',
      },
    });
  }
  return cache.publisher;
}

export function getReleaseDateController(eng: SearchEngine): DateFacet {
  const cache = getCache(eng);
  if (!cache.releaseDate) {
    const now = new Date();
    const toApi = (d: Date) => d.toISOString().split('.')[0] + 'Z';

    cache.releaseDate = buildDateFacet(eng, {
      options: {
        field: 'first_published_at',
        generateAutomaticRanges: false,
        currentValues: DATE_RANGE_KEYS.map((range) =>
          buildDateRange({
            start: toApi(new Date(now.getTime() - range.days * DAY_MS)),
            end: toApi(now),
            endInclusive: true,
          })
        ),
      },
    });
  }
  return cache.releaseDate;
}

export function getCategoryController(eng: SearchEngine): Facet {
  const cache = getCache(eng);
  if (!cache.category) {
    cache.category = buildFacet(eng, {
      options: {
        field: 'ec_category',
        numberOfValues: 150,
        sortCriteria: 'alphanumeric',
      },
    });
  }
  return cache.category;
}

export function getSaleController(eng: SearchEngine): Facet {
  const cache = getCache(eng);
  if (!cache.sale) {
    cache.sale = buildFacet(eng, {
      options: {
        field: 'ec_sale_filters',
        numberOfValues: 10,
      },
    });
  }
  return cache.sale;
}

/**
 * Reset all cached controllers for a given engine instance.
 * Call this when the engine is re-initialized (e.g., on auth change).
 */
export function resetFacetControllers(eng: SearchEngine): void {
  cacheByEngine.delete(eng);
}
